import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiResponse } from '@app/models/_base/apiResponse.model';
import { CourseAreasResponse, CourseAreaTop } from '@app/models/courses/courseArea.model';
import { CourseAreaDetail } from '@app/models/courses/courseAreaDetail.model';
import { CourseDetail } from '@app/models/courses/courseDetail.model';
import { CourseListResponse } from '@app/models/courses/courseListResponse.model';
import {
  PrimaryCourseFilterRequest,
  PrimaryCourseFiltersRequest
} from '@app/models/courses/filterRequest.primary.model';
import { SecondaryCourseFilterRequest } from '@app/models/courses/filterRequest.secondary.model';
import {
  CourseFilterResponse,
  CourseFiltersResponse
} from '@app/models/courses/filterResponse.model';
import { CourseFilterWarningsModel } from '@app/models/courses/filterWarnings.model';
import { InstitutionDetail } from '@app/models/courses/institutionDetail.model';
import { NavCourses } from '@app/models/courses/navCourses.model';
import { SelectCourseAreaResponse } from '@app/models/courses/selectCourseAreaResponse.model';
import { BehaviorSubject, Observable } from 'rxjs';

import { AppConfig } from '../app.config';
import { CurrentQuestionnaireService } from './current-questionnaire.service';
import { QuestionnaireService } from './questionnaire.service';
import { DcdArea, DcdAreaResponse, DcdAreasResponse } from '@app/models/dcd/area.model';

@Injectable()
export class CourseService {
  private categorySelectObservable = new BehaviorSubject<number>(2); // By default Solid is selected
  public categorySelected;

  private courseListCache: CourseListResponse;
  private observable: Observable<any>;

  private searchActiveTab: number;
  public navCourses: NavCourses = {
    pre: { id: null, courseName: null },
    nex: { id: null, courseName: null }
  };

  constructor(
    private http: HttpClient,
    private questionnaireService: QuestionnaireService,
    private currentQuestionnaire: CurrentQuestionnaireService
  ) {
    this.categorySelected = this.categorySelectObservable.asObservable();
  }

  updateCategorySelected(value: number) {
    this.categorySelectObservable.next(value);
    sessionStorage.setItem('searchActiveTab', JSON.stringify(value));
  }

  getCourseAreas(): Observable<CourseAreasResponse> {
    let url = AppConfig.ApiUrl + `coursearea/${this.currentQuestionnaire.get()}`;
    return this.http.get<CourseAreasResponse>(url);
  }

  selectCourseArea(courseAreaId: number): Observable<SelectCourseAreaResponse> {
    this.clearCourseListState();
    let url =
      AppConfig.ApiUrl + `coursearea/${this.currentQuestionnaire.get()}/select/${courseAreaId}`;
    return this.http.put<SelectCourseAreaResponse>(url, null);
  }

  deselectCourseArea(courseAreaId: number): Observable<SelectCourseAreaResponse> {
    this.clearCourseListState();
    let url =
      AppConfig.ApiUrl + `coursearea/${this.currentQuestionnaire.get()}/deselect/${courseAreaId}`;
    return this.http.put<SelectCourseAreaResponse>(url, null);
  }

  getCourseAreaDetail(courseAreaId: number): Observable<CourseAreaDetail> {
    let url =
      AppConfig.ApiUrl + `coursearea/${this.currentQuestionnaire.get()}/area/${courseAreaId}`;
    return this.http.get<CourseAreaDetail>(url);
  }

  getPrimaryCourseFilters(filters: PrimaryCourseFiltersRequest): Observable<CourseFiltersResponse> {
    let url = AppConfig.ApiUrl + `courselist/${this.currentQuestionnaire.get()}/primary`;
    return this.http.post<CourseFiltersResponse>(url, filters);
  }

  getPrimaryCourseFilterDetails(primaryFilterId: number): Observable<CourseFilterResponse> {
    let url =
      AppConfig.ApiUrl + `courselist/${this.currentQuestionnaire.get()}/primary/${primaryFilterId}`;
    return this.http.get<CourseFilterResponse>(url);
  }

  savePrimaryCourseFilter(filter: PrimaryCourseFilterRequest): Observable<CourseFilterResponse> {
    this.clearCourseListState();
    let url =
      AppConfig.ApiUrl +
      `courselist/${this.currentQuestionnaire.get()}/primary/${filter.filter.id}`;
    return this.http.put<CourseFilterResponse>(url, filter);
  }

  deletePrimaryCourseFilters(): Observable<CourseFiltersResponse> {
    this.clearCourseListState();
    let url = AppConfig.ApiUrl + `courselist/${this.currentQuestionnaire.get()}/primary`;
    return this.http.delete<CourseFiltersResponse>(url);
  }

  deletePrimaryCourseFilter(filterId: number) {
    this.clearCourseListState();
    let url =
      AppConfig.ApiUrl + `courselist/${this.currentQuestionnaire.get()}/primary/${filterId}`;
    return this.http.delete(url);
  }

  getFilterWarnings(filterId: number): Observable<CourseFilterWarningsModel> {
    let url =
      AppConfig.ApiUrl + `courselist/${this.currentQuestionnaire.get()}/warnings/${filterId}`;
    return this.http.get<CourseFilterWarningsModel>(url);
  }

  getCoursesBySecondaryFilters(
    options?: SecondaryCourseFilterRequest
  ): Observable<CourseListResponse> {
    if (!!this.observable) {
      return this.observable;
    }

    if (!!this.courseListCache) {
      return Observable.of(this.courseListCache);
    }

    let url = AppConfig.ApiUrl + `courselist/${this.currentQuestionnaire.get()}/secondary`;

    this.observable = this.http
      .post<CourseListResponse>(url, options)
      .map(data => {
        this.observable = null;
        this.courseListCache = data as CourseListResponse;
        return this.courseListCache;
      })
      .share();

    return this.observable;
  }

  clearCourseListCache() {
    this.courseListCache = null;
  }

  getNavCourses(courseId: string) {
    // clear navigation for upcoming course
    this.navCourses.pre.id = null;
    this.navCourses.nex.id = null;

    if (this.courseListCache != null) {
      let courseList = null;

      // Compile the courses depending on what have been selected prior
      switch (JSON.parse(sessionStorage.getItem('searchActiveTab'))) {
        case 0: {
          courseList = this.courseListCache.lower
            .concat(this.courseListCache.data)
            .concat(this.courseListCache.upper);
          break;
        }
        case 1: {
          courseList = this.courseListCache.lower;
          break;
        }
        case 2: {
          courseList = this.courseListCache.data;
          break;
        }
        case 3: {
          courseList = this.courseListCache.upper;
          break;
        }
      }
      // Create the navigations prev and next links
      for (var key = 0; key < courseList.length; key++) {
        let preCounter = key - 1;
        let nexCounter = key + 1;
        if (courseId == courseList[key].id) {
          if (key > 0) {
            // now we have previous
            this.navCourses.pre.id = courseList[preCounter].id;
            this.navCourses.pre.courseName = courseList[preCounter].courseName;
          } else {
            // Loop to last course
            this.navCourses.pre.id = courseList[courseList.length - 1].id;
            this.navCourses.pre.courseName = courseList[courseList.length - 1].courseName;
          }

          if (key < courseList.length - 1) {
            // now we have next
            this.navCourses.nex.id = courseList[nexCounter].id;
            this.navCourses.nex.courseName = courseList[nexCounter].courseName;
          } else {
            // Loop to first course
            this.navCourses.nex.id = courseList[0].id;
            this.navCourses.nex.courseName = courseList[0].courseName;
          }

          return this.navCourses;
        }
      }
    }

    return this.navCourses;
  }

  deleteSecondaryFilters(): Observable<ApiResponse> {
    this.clearCourseListState();
    let url = AppConfig.ApiUrl + `courselist/${this.currentQuestionnaire.get()}/secondary`;
    return this.http.delete<ApiResponse>(url);
  }

  getCourseDetail(courseId: string): Observable<CourseDetail> {
    let url = AppConfig.ApiUrl + `courselist/${this.currentQuestionnaire.get()}/course/${courseId}`;
    return this.http.get<CourseDetail>(url);
  }
  getInstitutionDetail(institutionId: string): Observable<InstitutionDetail> {
    let url =
      AppConfig.ApiUrl +
      `courselist/${this.currentQuestionnaire.get()}/institution/${institutionId}`;
    return this.http.get<InstitutionDetail>(url);
  }

  getCourseAreasTopFive(): Observable<CourseAreaTop> {
    let url = AppConfig.ApiUrl + `coursearea/${this.currentQuestionnaire.get()}/top/5`;
    return this.http.get<CourseAreaTop>(url);
  }

  clearCourseListState() {
    this.clearCourseListCache();
    sessionStorage.removeItem('searchTerm');
    sessionStorage.removeItem('searchSortOrder');
    sessionStorage.removeItem('searchActiveTab');
  }

  getRelatedDcds(): Observable<DcdAreasResponse> {
    let url = AppConfig.ApiUrl + `coursearea/${this.currentQuestionnaire.get()}/related-dcds`;
    return this.http.get<DcdAreasResponse>(url);
  }

  getAreaRelatedDcds(courseAreaId: number): Observable<DcdAreaResponse> {
    let url =
      AppConfig.ApiUrl +
      `coursearea/${this.currentQuestionnaire.get()}/area/${courseAreaId}/related-dcds`;
    return this.http.get<DcdAreaResponse>(url);
  }

  goToDcdArticle(articleId: number) {
    window.open(AppConfig.DcdUrl + `articles/${articleId}`, '_blank');
  }
}



// WEBPACK FOOTER //
// ./src/app/services/course.service.ts